// frontend/src/pages/admin/reports/RevenueReport.js
import React, { useState, useEffect } from 'react';
import axios from '../../../utils/axios';
import { toast } from 'react-toastify';
import {
  FiTrendingUp,
  FiDollarSign,
  FiCalendar
} from 'react-icons/fi';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const RevenueReport = () => {
  const [loading, setLoading] = useState(false);
  const [dailyData, setDailyData] = useState([]);
  const [dateRange, setDateRange] = useState('week');
  const [startDate, setStartDate] = useState(
    new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  );
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    if (dateRange !== 'custom') {
      handleDateRangeChange(dateRange);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (startDate && endDate) {
      fetchDailyRevenue();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  const handleDateRangeChange = (range) => {
    setDateRange(range);
    const today = new Date();
    let start, end;

    switch (range) {
      case 'today':
        start = end = today.toISOString().split('T')[0];
        break;
      case 'week':
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - 7);
        start = weekStart.toISOString().split('T')[0];
        end = today.toISOString().split('T')[0];
        break;
      case 'month':
        const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        start = monthStart.toISOString().split('T')[0];
        end = today.toISOString().split('T')[0];
        break;
      case 'custom':
        return;
      default:
        start = end = today.toISOString().split('T')[0];
    }

    setStartDate(start);
    setEndDate(end);
  };

  const fetchDailyRevenue = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('/api/reports/revenue/daily', {
        params: { start_date: startDate, end_date: endDate }
      });

      if (data.success) {
        setDailyData(data.data.daily || []);
      }
    } catch (error) {
      console.error('Fetch daily revenue error:', error);
      toast.error('Өдрийн орлого татахад алдаа гарлаа');
    } finally {
      setLoading(false);
    }
  };

  const handleCustomDateSearch = () => {
    if (!startDate || !endDate) {
      toast.warning('Эхлэх болон дуусах огноо сонгоно уу');
      return;
    }
    fetchDailyRevenue();
  };

  // Calculate total revenue with null safety
  const totalRevenue = dailyData.reduce((sum, day) => {
    const revenue = parseFloat(day.revenue) || 0;
    return sum + revenue;
  }, 0);

  const totalTransactions = dailyData.reduce((sum, day) => {
    const count = parseInt(day.transaction_count) || 0;
    return sum + count;
  }, 0);

  const averageRevenue = totalTransactions > 0 ? totalRevenue / totalTransactions : 0;

  return (
    <div className="space-y-6">
      {/* Date Range Filter */}
      <div className="card">
        <div className="flex items-center gap-2 mb-4">
          <FiCalendar className="text-gray-600" />
          <h3 className="font-semibold text-gray-900">Хугацаа сонгох</h3>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
          <button
            onClick={() => handleDateRangeChange('today')}
            className={`px-4 py-2 rounded-lg font-medium transition ${
              dateRange === 'today'
                ? 'bg-green-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Өнөөдөр
          </button>
          <button
            onClick={() => handleDateRangeChange('week')}
            className={`px-4 py-2 rounded-lg font-medium transition ${
              dateRange === 'week'
                ? 'bg-green-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            7 хоног
          </button>
          <button
            onClick={() => handleDateRangeChange('month')}
            className={`px-4 py-2 rounded-lg font-medium transition ${
              dateRange === 'month'
                ? 'bg-green-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Энэ сар
          </button>
          <button
            onClick={() => setDateRange('custom')}
            className={`px-4 py-2 rounded-lg font-medium transition ${
              dateRange === 'custom'
                ? 'bg-green-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Сонгох
          </button>
        </div>

        {dateRange === 'custom' && (
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Эхлэх огноо
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="input"
              />
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Дуусах огноо
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="input"
              />
            </div>
            <button
              onClick={handleCustomDateSearch}
              className="btn btn-primary"
            >
              Хайх
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="spinner-lg mx-auto mb-4"></div>
          <p className="text-gray-600">Тайлан бэлтгэж байна...</p>
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="card bg-gradient-to-br from-green-50 to-green-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600 font-semibold">Нийт борлуулалт</p>
                  <p className="text-2xl font-bold text-green-900 mt-1">
                    {totalRevenue.toLocaleString('mn-MN', {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 2
                    })}₮
                  </p>
                </div>
                <div className="w-12 h-12 bg-green-200 rounded-full flex items-center justify-center">
                  <FiDollarSign className="text-green-700" size={24} />
                </div>
              </div>
            </div>

            <div className="card bg-gradient-to-br from-blue-50 to-blue-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600 font-semibold">Гүйлгээний тоо</p>
                  <p className="text-2xl font-bold text-blue-900 mt-1">
                    {totalTransactions.toLocaleString()}
                  </p>
                </div>
                <div className="w-12 h-12 bg-blue-200 rounded-full flex items-center justify-center">
                  <FiTrendingUp className="text-blue-700" size={24} />
                </div>
              </div>
            </div>

            <div className="card bg-gradient-to-br from-purple-50 to-purple-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-purple-600 font-semibold">Дундаж дүн</p>
                  <p className="text-2xl font-bold text-purple-900 mt-1">
                    {averageRevenue.toLocaleString('mn-MN', {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 2
                    })}₮
                  </p>
                </div>
                <div className="w-12 h-12 bg-purple-200 rounded-full flex items-center justify-center">
                  <FiCalendar className="text-purple-700" size={24} />
                </div>
              </div>
            </div>
          </div>

          {/* Chart */}
          <div className="card">
            <h3 className="font-semibold text-gray-900 mb-4">📈 Өдрийн борлуулалт</h3>
            {dailyData.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                Мэдээлэл олдсонгүй
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={dailyData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={(date) => new Date(date).toLocaleDateString('mn-MN', { month: 'short', day: 'numeric' })}
                  />
                  <YAxis 
                    tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
                  />
                  <Tooltip 
                    formatter={(value) => `${parseFloat(value).toLocaleString()}₮`}
                    labelFormatter={(date) => new Date(date).toLocaleDateString('mn-MN')}
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="revenue" 
                    stroke="#10b981" 
                    strokeWidth={2}
                    name="Борлуулалт"
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Daily Table */}
          <div className="card">
            <h3 className="font-semibold text-gray-900 mb-4">📊 Өдрөөр задаргаа</h3>
            {dailyData.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                Мэдээлэл олдсонгүй
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Огноо</th>
                      <th className="text-center">Гүйлгээний тоо</th>
                      <th className="text-right">Нийт борлуулалт</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dailyData.map((day, index) => (
                      <tr key={index} className="table-row">
                        <td className="table-td">
                          {new Date(day.date).toLocaleDateString('mn-MN', { 
                            year: 'numeric',
                            month: 'long', 
                            day: 'numeric' 
                          })}
                        </td>
                        <td className="table-td text-center">
                          <span className="badge badge-blue">
                            {parseInt(day.transaction_count) || 0}
                          </span>
                        </td>
                        <td className="table-td text-right">
                          <span className="font-semibold text-green-600">
                            {(parseFloat(day.revenue) || 0).toLocaleString('mn-MN', {
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 2
                            })}₮
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default RevenueReport;